import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { X as XIcon, Loader2, Eye, EyeOff, Mail, User as UserIcon, ShieldCheck, Bell } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function DialogShell({ open, onClose, title, icon: Icon, children, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-[#111827] flex items-center gap-2">
            <Icon size={22} className="text-[#0055FF]" />
            {title}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid={`${testid}-close`}>
            <XIcon size={20} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function AccountDialog({ open, onClose, user, onUpdated }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [profileType, setProfileType] = useState('opc');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      setName(user.name || '');
      setPhone(user.phone || '');
      setProfileType(user.profile_type || 'opc');
    }
  }, [open, user]);

  const save = async () => {
    if (!name.trim()) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/auth/me`, { name: name.trim(), phone: phone.trim(), profile_type: profileType }, { withCredentials: true });
      toast.success('Profil mis à jour');
      onUpdated?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur mise à jour');
    } finally { setSaving(false); }
  };

  return (
    <DialogShell open={open} onClose={onClose} title="Mon compte" icon={UserIcon} testid="account-dialog">
      <div className="space-y-4">
        <div>
          <Label htmlFor="account-name">Nom complet *</Label>
          <Input id="account-name" value={name} onChange={e => setName(e.target.value)} maxLength={100} data-testid="account-name-input" />
        </div>
        <div>
          <Label>Email (non modifiable)</Label>
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border rounded px-3 py-2">
            <Mail size={14} />{user?.email}
          </div>
        </div>
        <div>
          <Label htmlFor="account-phone">Téléphone</Label>
          <Input id="account-phone" value={phone} onChange={e => setPhone(e.target.value)} maxLength={30}
            placeholder="Ex: 06 12 34 56 78" data-testid="account-phone-input" />
        </div>
        <div>
          <Label htmlFor="account-profile">Profil métier</Label>
          <select id="account-profile" value={profileType} onChange={e => setProfileType(e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white text-sm" data-testid="account-profile-select">
            <option value="opc">OPC (Ordonnancement Pilotage Coordination)</option>
            <option value="csps">CSPS (Coordination Sécurité)</option>
            <option value="opc_csps">Mixte OPC + CSPS</option>
          </select>
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[#0055FF] hover:bg-blue-700" data-testid="account-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}

export function SecurityDialog({ open, onClose }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setCurrent(''); setNext(''); setConfirm(''); } }, [open]);

  const save = async () => {
    if (!current) { toast.error('Mot de passe actuel requis'); return; }
    if (next.length < 8) { toast.error('Nouveau mot de passe : min 8 caractères'); return; }
    if (next !== confirm) { toast.error('Les mots de passe ne correspondent pas'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, { withCredentials: true });
      toast.success('Mot de passe modifié');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <DialogShell open={open} onClose={onClose} title="Sécurité — Changer mon mot de passe" icon={ShieldCheck} testid="security-dialog">
      <div className="space-y-4">
        <div>
          <Label htmlFor="pw-current">Mot de passe actuel *</Label>
          <div className="relative">
            <Input id="pw-current" type={showCurrent ? 'text' : 'password'} value={current}
              onChange={e => setCurrent(e.target.value)} data-testid="pw-current-input" />
            <button type="button" onClick={() => setShowCurrent(v => !v)} className="absolute right-2 top-2 text-gray-400 hover:text-gray-700">
              {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div>
          <Label htmlFor="pw-new">Nouveau mot de passe (min 8) *</Label>
          <div className="relative">
            <Input id="pw-new" type={showNext ? 'text' : 'password'} value={next}
              onChange={e => setNext(e.target.value)} data-testid="pw-new-input" />
            <button type="button" onClick={() => setShowNext(v => !v)} className="absolute right-2 top-2 text-gray-400 hover:text-gray-700">
              {showNext ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div>
          <Label htmlFor="pw-confirm">Confirmer le nouveau mot de passe *</Label>
          <Input id="pw-confirm" type={showNext ? 'text' : 'password'} value={confirm}
            onChange={e => setConfirm(e.target.value)} data-testid="pw-confirm-input" />
          {confirm && next && confirm !== next && (
            <p className="text-xs text-red-600 mt-1">Les mots de passe ne correspondent pas</p>
          )}
        </div>
        <div className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded p-2">
          💡 Choisissez un mot de passe unique, comportant au moins 1 lettre et 1 chiffre. Ne le réutilisez pas sur d'autres sites.
        </div>
        <div className="flex gap-2 pt-1">
          <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[#0055FF] hover:bg-blue-700" data-testid="pw-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Mettre à jour'}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}

const NOTIF_ITEMS = [
  { key: 'email_weekly_digest',       label: 'Résumé hebdomadaire',     desc: 'Chaque lundi : activité de vos chantiers, réserves ouvertes, tâches à venir' },
  { key: 'email_new_feedback',        label: 'Nouveaux retours pilotes', desc: 'Recevez un mail à chaque feedback envoyé (admins uniquement)' },
  { key: 'email_subscription_reminders', label: 'Rappels abonnement',    desc: 'Alerte J-7 et J-1 avant expiration de votre abonnement' },
  { key: 'email_pilot_updates',       label: 'Actualités pilotes',       desc: 'Nouveautés produit, tips CSPS/OPC, invitations webinaires' },
];

export function NotificationsDialog({ open, onClose }) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/user/notification-preferences`, { withCredentials: true })
      .then(r => setPrefs(r.data))
      .catch(() => toast.error('Erreur chargement préférences'))
      .finally(() => setLoading(false));
  }, [open]);

  const toggle = (k) => setPrefs(p => ({ ...p, [k]: !p[k] }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/notification-preferences`, prefs, { withCredentials: true });
      toast.success('Préférences enregistrées');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <DialogShell open={open} onClose={onClose} title="Notifications" icon={Bell} testid="notifications-dialog">
      {loading || !prefs ? (
        <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
      ) : (
        <div className="space-y-3">
          {NOTIF_ITEMS.map(item => (
            <label key={item.key} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer" data-testid={`notif-${item.key}`}>
              <input type="checkbox" checked={!!prefs[item.key]} onChange={() => toggle(item.key)}
                className="mt-0.5 h-5 w-5 accent-[#0055FF]" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-[#111827]">{item.label}</p>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            </label>
          ))}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button onClick={save} disabled={saving} className="flex-1 bg-[#0055FF] hover:bg-blue-700" data-testid="notif-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </div>
        </div>
      )}
    </DialogShell>
  );
}
